import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface ResultMetric {
  label: string;
  score: number;
  maxScore: number;
  status: 'excellent' | 'good' | 'fair' | 'needs-improvement';
}

interface ResultsCardProps {
  overallScore: number;
  metrics: ResultMetric[];
  readinessLevel: string;
  recommendations: string[];
}

const ResultsCard = ({ overallScore, metrics, readinessLevel, recommendations }: ResultsCardProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent':
        return 'text-accent';
      case 'good':
        return 'text-primary';
      case 'fair':
        return 'text-warning';
      case 'needs-improvement':
        return 'text-destructive';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'excellent':
        return 'bg-accent/10';
      case 'good':
        return 'bg-primary/10';
      case 'fair':
        return 'bg-warning/10';
      case 'needs-improvement':
        return 'bg-destructive/10';
      default:
        return 'bg-muted';
    }
  };

  return (
    <div className="bg-card rounded-lg p-8 shadow-subtle border border-border">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-primary to-accent mb-4">
          <span className="text-4xl font-display font-bold text-white">{overallScore}</span>
        </div>
        <h2 className="text-2xl font-display font-bold text-secondary mb-2">AI Readiness Score</h2>
        <p className="text-lg text-muted-foreground">Your organization is <span className="font-semibold text-primary">{readinessLevel}</span></p>
      </div>

      <div className="space-y-4 mb-8">
        <h3 className="text-lg font-display font-semibold text-secondary">Detailed Breakdown</h3>
        {metrics.map((metric, index) => (
          <div key={index} className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">{metric.label}</span>
              <span className={`text-sm font-semibold ${getStatusColor(metric.status)}`}>
                {metric.score}/{metric.maxScore}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${getStatusBg(metric.status)}`}
                  style={{ width: `${(metric.score / metric.maxScore) * 100}%` }}
                />
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded ${getStatusBg(metric.status)} ${getStatusColor(metric.status)}`}>
                {metric.status.replace('-', ' ').toUpperCase()}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-display font-semibold text-secondary">Key Recommendations</h3>
        <div className="space-y-3">
          {recommendations.map((recommendation, index) => (
            <div key={index} className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
              <Icon name="LightBulbIcon" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <p className="text-sm text-foreground">{recommendation}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResultsCard;